import useIsMobile from 'utils/hooks/useIsMobile'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import {Button, TextField} from '@mui/material'
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers-pro'
import {AdapterDayjs} from '@mui/x-date-pickers-pro/AdapterDayjs'
import {envImagePath} from 'GLOBAL/envVariables'
import {KTSVG} from '_metronic/helpers'
import {yMarker} from 'assets/imageDeclear'
import dayjs from 'dayjs'
import {useEffect, useRef, useState} from 'react'
import {useSelector} from 'react-redux'
import {failAlert, validationAlert} from 'utils/CustomAlert/Alert'
import {getClientInfo} from 'utils/apiCalling/api'
import {globalSerialNumberFormatWithHyphens} from 'utils/const/globalConst'

const infoRowStyle: React.CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: '10px 0',
  borderBottom: '1px solid #eee',
}

const CustomerClient = () => {
  const mapRef = useRef<any>(null)
  const markerRef = useRef<any>(null)
  const navigateDirectionRef = useRef<'prev-day' | 'next-day' | null>(null)
  const isMobile = useIsMobile()
  const officeInfo = useSelector((state: any) => state.officeInfo)
  const userInfo = useSelector((state: any) => state.userInfo.userInfo)
  const [clientInfo, setClientInfo] = useState<any>(null)
  const [date, setDate] = useState<any>(dayjs().format('YYYYMMDD'))
  const submitData = {
    id: userInfo?.userId,
    customerNumber: userInfo?.userName,
    date: date,
  }
  const [imagePath, setImagePath] = useState<any>()
  const [listNum, setListNum] = useState<any>(0)
  const [digitalNum, setDigitalNum] = useState<any>(0)

  const getClientInfoCall = async () => {
    await getClientInfo(officeInfo, submitData, {
      success: (res) => {
        if (!res.checkList || res.checkList.length === 0) {
          if (navigateDirectionRef.current === 'prev-day') {
            const prevDay = dayjs(date, 'YYYYMMDD').subtract(1, 'day')
            if (prevDay.format('YYYYMMDD') < dayjs().subtract(3, 'month').format('YYYYMMDD')) {
              navigateDirectionRef.current = null
              validationAlert('더 이상 이전 검침 데이터가 없습니다.', 'warning')
              return
            }
            setDate(prevDay.format('YYYYMMDD'))
            return
          }
          if (navigateDirectionRef.current === 'next-day') {
            const nextDay = dayjs(date, 'YYYYMMDD').add(1, 'day')
            if (nextDay.format('YYYYMMDD') > dayjs().format('YYYYMMDD')) {
              navigateDirectionRef.current = null
              validationAlert('더 이상 다음 검침 데이터가 없습니다.', 'warning')
              return
            }
            setDate(nextDay.format('YYYYMMDD'))
            return
          }
          validationAlert(
            `${dayjs(date).format('YYYY년 MM월 DD일')} 검침 데이터가 없습니다.`,
            'warning'
          )
          setClientInfo((prev: any) => ({
            ...prev,
            checkList: [],
            checkValue: null,
          }))
          setImagePath('')
          setDigitalNum('')
          return
        }

        setClientInfo(res)

        const targetIndex =
          navigateDirectionRef.current === 'next-day' ? res.checkList.length - 1 : 0
        navigateDirectionRef.current = null
        setListNum(targetIndex)

        if (res.deviceType === 'analog') {
          setImagePath(res.checkList[targetIndex]?.filePath || '')
        } else if (res.deviceType === 'digital') {
          setDigitalNum(res.checkList[targetIndex]?.checkValue || '')
        }
      },
      fail: () => failAlert(),
      loading: () => {},
    })
  }

  const handlePrevious = () => {
    if (!clientInfo?.checkList) return
    if (listNum === clientInfo.checkList.length - 1) {
      const prevDay = dayjs(date, 'YYYYMMDD').subtract(1, 'day')
      if (prevDay.format('YYYYMMDD') < dayjs().subtract(3, 'month').format('YYYYMMDD')) {
        validationAlert('3개월 이전 데이터는 조회할 수 없습니다.', 'warning')
        return
      }
      navigateDirectionRef.current = 'prev-day'
      setDate(prevDay.format('YYYYMMDD'))
      return
    }
    const nextIndex = listNum + 1
    if (clientInfo.checkList[nextIndex]) {
      if (clientInfo.deviceType === 'analog') {
        setImagePath(clientInfo.checkList[nextIndex]?.filePath || '')
      } else {
        setDigitalNum(clientInfo.checkList[nextIndex]?.checkValue || '')
      }
      setListNum(nextIndex)
    }
  }

  const handleNext = () => {
    if (!clientInfo?.checkList) return
    if (listNum === 0) {
      const nextDay = dayjs(date, 'YYYYMMDD').add(1, 'day')
      if (nextDay.format('YYYYMMDD') > dayjs().format('YYYYMMDD')) {
        validationAlert('오늘 이후 날짜는 조회할 수 없습니다.', 'warning')
        return
      }
      navigateDirectionRef.current = 'next-day'
      setDate(nextDay.format('YYYYMMDD'))
      return
    }
    const nextIndex = listNum - 1
    if (clientInfo.checkList[nextIndex]) {
      if (clientInfo.deviceType === 'analog') {
        setImagePath(clientInfo.checkList[nextIndex]?.filePath || '')
      } else {
        setDigitalNum(clientInfo.checkList[nextIndex]?.checkValue || '')
      }
      setListNum(nextIndex)
    }
  }

  useEffect(() => {
    getClientInfoCall()
  }, [date])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement).tagName
      if (tag === 'INPUT' || tag === 'TEXTAREA') return
      if (e.key === 'ArrowLeft') handlePrevious()
      if (e.key === 'ArrowRight') handleNext()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [handlePrevious, handleNext])

  useEffect(() => {
    if (!clientInfo?.latitude || !clientInfo?.longitude) return
    if (!window.kakao?.maps) {
      console.log('[KakaoMap] kakao maps not loaded')
      return
    }

    const {latitude, longitude} = clientInfo
    const container = document.getElementById('map')
    if (!container) return

    if (!mapRef.current) {
      mapRef.current = new window.kakao.maps.Map(container, {
        center: new window.kakao.maps.LatLng(latitude, longitude),
        mapTypeId: window.kakao.maps.MapTypeId.HYBRID,
        level: 1,
        draggable: false,
        scrollwheel: false,
        zoomable: false,
      })
    }

    const position = new window.kakao.maps.LatLng(latitude, longitude)

    if (!markerRef.current) {
      markerRef.current = new window.kakao.maps.Marker({
        position,
        image: new window.kakao.maps.MarkerImage(yMarker, new window.kakao.maps.Size(40, 59)),
      })
      markerRef.current.setMap(mapRef.current)
    } else {
      markerRef.current.setPosition(position)
    }

    mapRef.current.setCenter(position)
  }, [clientInfo])

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout>
    const handleResize = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        if (!mapRef.current || !clientInfo?.latitude || !clientInfo?.longitude) return
        mapRef.current.relayout()
        mapRef.current.setCenter(new window.kakao.maps.LatLng(clientInfo.latitude, clientInfo.longitude))
      }, 200)
    }
    window.addEventListener('resize', handleResize)
    return () => {
      clearTimeout(timer)
      window.removeEventListener('resize', handleResize)
    }
  }, [clientInfo])

  const buttonSx = {
    backgroundColor: '#007bff',
    color: '#fff',
    '&:hover': {backgroundColor: '#0056b3'},
    '&.Mui-disabled': {backgroundColor: '#e0e0e0', color: 'rgba(0,0,0,0.26)'},
  }

  const customerFields = [
    {label: '수용가 이름', value: clientInfo?.customerName},
    {label: '수용가 번호', value: userInfo?.userName},
    {label: '계량기 번호', value: clientInfo?.meterNumber},
    {label: '수용가 주소', value: clientInfo?.address},
    {label: '담당자 이름', value: clientInfo?.managerName},
  ]

  return (
    <div className={`card flex-grow-1 ${isMobile ? 'my-3' : ''}`} id='client-page'>
      <div className='card-header'>
        <div className='card-title m-0'>
          <h3 className='fw-bolder m-0'>수용가의 정보를 확인해보세요.</h3>
        </div>
      </div>
      {clientInfo ? (
        <div className={`card-body ${isMobile ? 'p-6' : 'p-9'}`}>
          <div
            style={{
              display: 'flex',
              flexDirection: isMobile ? 'column' : 'row',
              height: isMobile ? undefined : '100%',
              gap: '16px',
            }}
          >
            <div style={{flex: isMobile ? undefined : '0 0 50%'}}>
              <div
                id='map'
                style={{width: '100%', height: isMobile ? '200px' : '100%'}}
                className='rounded-3'
              />
            </div>

            <div
              style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                overflow: 'auto',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: '16px',
                  flex: 1,
                }}
              >
                {/* 수용가 + 단말기 정보 */}
                <div style={{flex: 1}}>
                  <h2 className='text-center text-primary mb-2' style={{fontSize: '22px'}}>
                    수용가 정보
                  </h2>
                  {customerFields.map(({label, value}) => (
                    <div key={label} style={infoRowStyle}>
                      <span style={{color: '#888', fontSize: '15px'}}>{label}</span>
                      <span style={{fontWeight: 600, fontSize: '16px'}}>{value}</span>
                    </div>
                  ))}

                  <h2
                    className='text-center text-primary mb-2'
                    style={{fontSize: '22px', marginTop: '20px'}}
                  >
                    단말기 정보
                  </h2>
                  <div style={infoRowStyle}>
                    <span style={{color: '#888', fontSize: '15px'}}>단말기 S/N</span>
                    <span style={{fontWeight: 600, fontSize: '16px'}}>
                      {globalSerialNumberFormatWithHyphens(clientInfo?.serialNumber)}
                    </span>
                  </div>
                </div>

                {/* 검침 정보 */}
                <div style={{flex: 1, display: 'flex', flexDirection: 'column', gap: '16px'}}>
                  <h2 className='text-center text-primary mb-2' style={{fontSize: '22px'}}>
                    검침 정보
                  </h2>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label='날짜 선택'
                      onChange={(newDate) => {
                        setDate(dayjs(newDate).format('YYYYMMDD'))
                      }}
                      value={dayjs(date, 'YYYYMMDD')}
                      minDate={dayjs().subtract(3, 'month')}
                      maxDate={dayjs()}
                      disableFuture={true}
                      inputFormat={'YYYY년 MM월 DD일'}
                      renderInput={(params) => (
                        <TextField {...params} variant={'outlined'} fullWidth />
                      )}
                    />
                  </LocalizationProvider>

                  <div className='text-center'>
                    {clientInfo?.deviceType === 'analog' && clientInfo?.checkValue !== null ? (
                      <div style={{position: 'relative', width: '100%', height: '100%'}}>
                        <img
                          src={envImagePath + imagePath}
                          alt={`${clientInfo?.customerName}님의 검침 이미지`}
                          style={{
                            maxWidth: '480px',
                            width: '100%',
                            height: 'auto',
                            display: 'block',
                            margin: '0 auto',
                            transform:
                              (Number(clientInfo?.rotate) > 90 &&
                                Number(clientInfo?.rotate) < 270) ||
                              (Number(clientInfo?.rotate) < -90 &&
                                Number(clientInfo?.rotate) > -270)
                                ? `rotate(${clientInfo?.rotate}deg)`
                                : `rotate(0deg)`,
                          }}
                        />
                      </div>
                    ) : null}

                    {clientInfo?.deviceType === 'digital' ? (
                      <TextField
                        variant={'outlined'}
                        inputProps={{style: {textAlign: 'center'}}}
                        value={digitalNum}
                        label={'검침값'}
                        InputProps={{readOnly: true}}
                      />
                    ) : null}
                  </div>

                  {clientInfo?.checkValue === null ? (
                    <div style={{...infoRowStyle, justifyContent: 'center'}}>
                      <span style={{color: '#888', fontSize: '15px'}}>
                        {dayjs(date).format('YYYY년 MM월 DD일')} 검침 대기중
                      </span>
                    </div>
                  ) : (
                    <div style={infoRowStyle}>
                      <span style={{color: '#888', fontSize: '15px'}}>검침일시</span>
                      <span style={{fontWeight: 600, fontSize: '16px'}}>
                        {clientInfo?.checkList[listNum]?.createAt
                          ? dayjs(clientInfo?.checkList[listNum]?.createAt).format(
                              'YYYY년 MM월 DD일 HH시 mm분'
                            )
                          : '검침 데이터 확인중'}
                      </span>
                    </div>
                  )}

                  <div style={{display: 'flex', justifyContent: 'space-between', gap: '8px'}}>
                    <Button
                      variant='contained'
                      size='large'
                      startIcon={<ArrowBackIcon />}
                      sx={buttonSx}
                      onClick={handlePrevious}
                    >
                      이전
                    </Button>
                    <Button
                      disabled={date === dayjs().format('YYYYMMDD') && listNum === 0}
                      variant='contained'
                      size='large'
                      endIcon={<ArrowForwardIcon />}
                      sx={buttonSx}
                      onClick={handleNext}
                    >
                      다음
                    </Button>
                  </div>
                </div>
              </div>

              <div className='notice d-flex bg-light-primary rounded border-primary border border-dashed p-2 mt-4'>
                <KTSVG
                  path='icons/duotune/general/gen044.svg'
                  className='svg-icon-2tx svg-icon-warning me-4'
                />
                <div className='d-flex flex-stack flex-grow-1'>
                  <div className='fw-bold'>
                    <h4 className='text-gray-800 fw-bold fs-2'>
                      <u>미터리움 일일 검침</u>
                    </h4>
                    <div className='fs-4 text-gray-600 d-flex flex-column gap-2'>
                      <span>
                        미터리움[스마트 디지털이미지 원격검침시스템 플랫폼]은 개인정보처리방침을
                        준수하고 있으며, 회원가입 시 동의한 이용약관에 의거하여 서비스를 제공합니다.
                      </span>
                      <a
                        className='fw-bolder'
                        href='https://young-it.co.kr'
                        target={'_blank'}
                        rel='noreferrer'
                      >
                        YOUNG-IT .inc
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className='card-body p-9'>
          <div className='d-flex justify-content-center align-items-center h-100'>
            <div className={'row gap-4 align-items-center justify-content-center text-center'}>
              <div className={'spinner-border spinner-border-m'}></div>
              <span className='text-primary fs-2 fw-bold'>데이터를 불러오고 있습니다.</span>
              <span className={'text-danger fw-bold fs-5'}>
                대기가 장시간 지연될경우 영아이티 개발팀에 문의해주세요.
              </span>
              <span className={'fs-5'}>문의 : 042-932-0170</span>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
export default CustomerClient
